import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@travellegacy/nexops-design-system';
import { Users2 } from 'lucide-react';
import type { WorkloadItem } from '../api/demands.api';
import { formatDurationRich } from '../utils/format-duration';

interface Props {
  workload: WorkloadItem[];
  stuckThresholdMs: number | undefined;
  isLoading: boolean;
}

/**
 * Carga atual por responsável — ciclos abertos agora, com maior tempo
 * decorrido e nº de travadas. Usa barras horizontais proporcionais ao
 * maior activeCount para dar noção visual de sobrecarga.
 */
export function WorkloadCard({ workload, stuckThresholdMs, isLoading }: Props) {
  const maxActive = workload.reduce((acc, w) => Math.max(acc, w.activeCount), 0);
  const threshold = stuckThresholdMs ?? 8 * 60 * 60 * 1000;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users2 className="h-5 w-5" />
          Carga atual por responsável
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Carregando…</p>
        ) : workload.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Ninguém com demanda em andamento no momento.
          </p>
        ) : (
          <ul className="space-y-3">
            {workload.map((w) => {
              const widthPct = maxActive > 0 ? (w.activeCount / maxActive) * 100 : 0;
              const isOverloaded = w.stuckCount > 0 || w.oldestElapsedMs >= threshold;
              return (
                <li key={w.responsibleName} className="space-y-1">
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate font-medium">{w.responsibleName}</span>
                      {w.stuckCount > 0 && (
                        <Badge variant="error" className="font-normal">
                          {w.stuckCount} travada{w.stuckCount > 1 ? 's' : ''}
                        </Badge>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-3 tabular-nums text-xs text-muted-foreground">
                      <span>
                        <strong className="text-foreground">{w.activeCount}</strong> ativa
                        {w.activeCount === 1 ? '' : 's'}
                      </span>
                      <span title="Tempo do ciclo aberto mais antigo">
                        maior: {formatDurationRich(w.oldestElapsedMs)}
                      </span>
                    </div>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className={`h-full rounded-full transition-all ${
                        isOverloaded
                          ? 'bg-red-500/80 dark:bg-red-500/70'
                          : w.oldestElapsedMs >= threshold / 2
                            ? 'bg-amber-500/80 dark:bg-amber-500/70'
                            : 'bg-emerald-500/80 dark:bg-emerald-500/70'
                      }`}
                      style={{ width: `${widthPct}%` }}
                      aria-hidden="true"
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
